<template>
  <!-- Component: LayoutHeader -->
  <div class="sticky top-0 z-[1000]">
    <StwMenubar :model="items" breakpoint="1279px">
      <template #start>
        <RouterLink to="/">
          <div
            class="flex cursor-pointer items-center gap-2 py-[3px] pr-2 font-semibold"
          >
            <img
              class="h-[40px] w-auto"
              :src="PLATFORM_LOGO_URL"
              alt="CityU JCC"
            />
          </div>
        </RouterLink>
      </template>

      <template #end>
        <div class="flex items-center gap-1 xl:gap-2">
          <NotificationBell v-if="user" />

          <div v-if="!user" class="flex gap-2">
            <StwButton size="sm" @click="handleAuth">
              <i class="pi pi-user" />
              <span class="hidden xl:block">Login / Register</span>
            </StwButton>
          </div>

          <div v-else class="flex items-center gap-2">
            <StwButton size="sm" color="primary" @click="uploadVisible = true">
              <i class="pi pi-upload" />
              <span class="hidden xl:block">New Model</span>
            </StwButton>

            <!-- TODO: refactor into avatar component -->
            <RouterLink
              class="flex items-center rounded-md p-0.5 transition hover:![background-color:var(--stw-glass-bg-hover)]"
              to="/settings"
            >
              <img
                class="h-8 w-8 rounded-md object-cover ring-1 [--tw-ring-color:var(--stw-glass-border)]"
                :src="user?.avatar || PLATFORM_MARK_URL"
                alt="User Avatar"
              />
            </RouterLink>
          </div>
        </div>
      </template>
    </StwMenubar>
  </div>

  <Dialog
    v-model:visible="uploadVisible"
    class="p-dialog-maximized"
    header="Upload"
    modal
    :pt="{
      root: { class: '!bg-surface-50 !text-surface-950' },
      header: {
        class: '!bg-surface-50 !text-surface-950 border-b border-surface-300',
      },
      title: { class: '!text-surface-950' },
      content: { class: '!bg-surface-50 !text-surface-950' },
      pcCloseButton: {
        root: { class: '!text-surface-600 hover:!text-surface-950' },
      },
      mask: { class: 'backdrop-blur-sm' },
    }"
  >
    <Upload @upload-complete="handleUploadComplete" />
  </Dialog>
</template>

<script setup lang="ts">
import { USER_ROLE } from '@stw/types';
import { storeToRefs } from 'pinia';
import { Dialog } from 'primevue';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import NotificationBell from '@/components/NotificationBell.vue';
import Upload from '@/components/Upload.vue';
import { StwButton, StwMenubar } from '@/components/stw';
import { PLATFORM_LOGO_URL, PLATFORM_MARK_URL } from '@/constants/branding';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const authStore = useAuthStore();
const route = useRoute();

const { user } = storeToRefs(authStore);

// Same effective-role rule as backend's isProfOrAdmin (utils/auth.ts):
// is_admin first, then user_role_id.
const isProfOrAdmin = computed(
  () =>
    !!user.value &&
    (user.value.is_admin || user.value.user_role_id === USER_ROLE.PROF)
);

const getBaseMenuItems = () => [
  {
    label: 'Explore',
    icon: 'pi pi-search',
    command: () => router.push('/'),
    path: '/',
  },
  {
    label: 'My Dashboard',
    icon: 'pi pi-home',
    command: () => router.push('/dashboard'),
    path: '/dashboard',
  },
  {
    label: 'Groups',
    icon: 'pi pi-users',
    command: () => router.push('/user-groups'),
    path: '/user-groups',
  },
  ...(isProfOrAdmin.value
    ? [
        {
          label: 'Students',
          icon: 'pi pi-th-large',
          command: () => router.push('/student-directory'),
          path: '/student-directory',
        },
      ]
    : []),
  {
    label: 'Help',
    icon: 'pi pi-question-circle',
    command: () => router.push('/tutorial'),
    path: '/tutorial',
  },
  {
    label: 'Feedback',
    icon: 'pi pi-envelope',
    command: () => router.push('/contact'),
    path: '/contact',
  },
];

// Compute items with current route highlighting
const computeItems = () => {
  return getBaseMenuItems().map((item) => {
    if (!item.path) return item;

    const isCurrentPage = route.path === item.path;
    return {
      ...item,
      // Was a hardcoded text-primary-400 (fine on the old dark nav, ~1.84:1
      // and unreadable once the nav went light) — --p-highlight-color is
      // the theme-aware "emphasized text" token instead.
      class: isCurrentPage ? 'text-[var(--p-highlight-color)]' : '',
    };
  });
};

const items = ref(computeItems());

// Watch route changes to update items
watch(
  () => route.path,
  () => {
    items.value = computeItems();
  }
);

// Also recompute on login/logout — Admin Panel's visibility depends on
// the user's role, not just the current route.
watch(user, () => {
  items.value = computeItems();
});

const uploadVisible = ref(false);

const handleAuth = () => {
  let redirectPath = '/'; // Default redirect path

  // Check if the current route is the auth page itself
  if (route.path === '/auth') {
    // If on auth page, use its existing redirect query if available, otherwise default
    if (typeof route.query.redirect === 'string' && route.query.redirect) {
      redirectPath = route.query.redirect;
    } else {
      // If no redirect query on auth page, or it's not a string, default to home
      redirectPath = '/';
    }
  } else {
    // If on any other page, use its fullPath as the redirect target
    redirectPath = route.fullPath;
  }
  router.push({ path: '/auth', query: { redirect: redirectPath } });
};

const handleUploadComplete = () => {
  uploadVisible.value = false;

  if (route.fullPath === '/dashboard') {
    router.push('/dashboard?refresh=true');
  } else {
    router.push('/dashboard');
  }
};
</script>
